import React, { useEffect, useMemo, useState } from "react";

/**
 * Speedy Phrase Maker (PT-BR)
 * Single-file React component. Tailwind-friendly. No external deps.
 */

interface PhraseItem {
  phrase_pt: string;
  explain_pt: string;
  translate_en: string;
  usage_tip: string;
  register: string;
  tags: string[];
  alt_variants: string[];
}

interface IconProps {
  className?: string;
  [key: string]: any;
}

// Small inline SVG icons (no external packages)
const IconWand = (props: IconProps) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...props}>
    <path d="M15 4l5 5M3 21l9-9"/>
    <path d="M8 3h2M3 8h2M13 8h2M8 13h2" strokeLinecap="round"/>
  </svg>
);
const IconCopy = (props: IconProps) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...props}>
    <rect x="9" y="9" width="13" height="13" rx="2"/>
    <rect x="2" y="2" width="13" height="13" rx="2"/>
  </svg>
);
const IconPlay = (props: IconProps) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...props}>
    <polygon points="6,4 20,12 6,20" fill="currentColor"/>
  </svg>
);
const IconDownload = (props: IconProps) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...props}>
    <path d="M12 3v12"/>
    <path d="M7 10l5 5 5-5"/>
    <rect x="3" y="18" width="18" height="3" rx="1.5"/>
  </svg>
);

export default function PhraseMakerApp() {
  // Form state
  const [topic, setTopic] = useState("");
  const [context, setContext] = useState("");
  const [tone, setTone] = useState("neutral");
  const [register, setRegister] = useState("informal"); // informal | neutral | formal
  const [level, setLevel] = useState("B1"); // CEFR A2/B1/B2
  const [count, setCount] = useState(8); // 4..14
  const [slang, setSlang] = useState(true);
  const [businessSafe, setBusinessSafe] = useState(false);
  const [temperature, setTemperature] = useState(0.7);
  const [mockMode, setMockMode] = useState(false);

  // Results
  const [items, setItems] = useState<PhraseItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Persist basic prefs
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("pm_prefs") || "null");
      if (saved) {
        setTone(saved.tone ?? "neutral");
        setRegister(saved.register ?? "informal");
        setLevel(saved.level ?? "B1");
        setCount(saved.count ?? 8);
        setSlang(saved.slang ?? true);
        setBusinessSafe(saved.businessSafe ?? false);
        setTemperature(saved.temperature ?? 0.7);
      }
      const savedTopic = localStorage.getItem("pm_topic");
      const savedContext = localStorage.getItem("pm_context");
      if (savedTopic) setTopic(savedTopic);
      if (savedContext) setContext(savedContext);
      const savedItems = JSON.parse(localStorage.getItem("pm_items") || "null");
      if (savedItems) setItems(savedItems);
    } catch {}
  }, []);

  useEffect(() => {
    const prefs = { tone, register, level, count, slang, businessSafe, temperature };
    localStorage.setItem("pm_prefs", JSON.stringify(prefs));
    localStorage.setItem("pm_topic", topic);
    localStorage.setItem("pm_context", context);
    localStorage.setItem("pm_items", JSON.stringify(items));
  }, [tone, register, level, count, slang, businessSafe, temperature, topic, context, items]);

  const hasClaude = true; // Always available via API

  const systemPrompt = useMemo(() => `You are a bilingual Brazilian Portuguese (pt-BR) phrase generator. 
Return ONLY strict JSON with this shape (no prose, no markdown fences):
{
  "phrases": [
    {
      "phrase_pt": string,                 // ≤ 12 words, natural pt-BR
      "explain_pt": string,                // one-sentence explanation in pt-BR
      "translate_en": string,              // natural English (not word-for-word)
      "usage_tip": string,                 // when/how to use it
      "register": "informal"|"neutral"|"formal",
      "tags": string[],                    // e.g., ["tone:playful", "slang"]
      "alt_variants": string[]             // 1-2 short alternative phrasings in pt-BR
    }
  ]
}
Guidelines:
- Use Brazilian Portuguese.
- Keep phrases short and usable in chat/WhatsApp.
- If slang=true, use mild, widely understood slang; if businessSafe=true, avoid slang and keep polite.
- Respect requested tone, register, and CEFR level for vocab simplicity.
- Avoid profanity, politics, or sensitive topics.
- Produce exactly the requested number of items.
`, []);

  function buildUserPrompt() {
    const brief = [
      topic ? `Topic: ${topic}` : null,
      context ? `Context: ${context}` : null,
      `Tone: ${tone}`,
      `Register: ${register}`,
      `Level: ${level}`,
      `Count: ${count}`,
      `Slang: ${slang}`,
      `BusinessSafe: ${businessSafe}`,
    ].filter(Boolean).join("\n");

    return `Create ${count} concise Brazilian Portuguese phrases for the scenario.\n${brief}`;
  }

  function surpriseMe() {
    const tones = ["neutral","playful","romantic","polite","bossy","ironic","friendly","supportive","enthusiastic"];
    const regs = ["informal","neutral","formal"];
    setTone(tones[Math.floor(Math.random()*tones.length)]);
    setRegister(regs[Math.floor(Math.random()*regs.length)]);
    setLevel(["A2","B1","B2"][Math.floor(Math.random()*3)]);
    setCount(6 + Math.floor(Math.random()*7));
    setSlang(Math.random() > 0.5);
    setBusinessSafe(Math.random() > 0.6);
  }

  async function speak(text: string, lang = "pt-BR") {
    try {
      const u = new SpeechSynthesisUtterance(text);
      u.lang = lang;
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(u);
    } catch {}
  }

  function copy(text: string) {
    navigator.clipboard?.writeText(text);
  }

  function getDemoResponse() {
    const sample = {
      phrases: Array.from({ length: count }).map((_, i) => ({
        phrase_pt: i % 2 === 0 ? `Bora ${topic || "fazer isso"}? ${i+1}` : `Fechou, partiu ${topic || "isso"}!`,
        explain_pt: "Expressão informal brasileira para confirmar ou propor algo.",
        translate_en: i % 2 === 0 ? `Wanna ${topic || "do this"}?` : "Deal, let's go!",
        usage_tip: "Use com amigos em chat casual.",
        register,
        tags: [tone ? `tone:${tone}` : "tone:neutral", slang ? "slang" : ""].filter(Boolean),
        alt_variants: ["Pode ser?", "Topa?", "Vamos nessa?"]
      }))
    };
    return JSON.stringify(sample);
  }

  function exportJSON() {
    const blob = new Blob([JSON.stringify({ phrases: items }, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "phrases_ptbr.json";
    a.click();
    URL.revokeObjectURL(url);
  }

  function coerceResponseToText(resp: any) {
    // Try common shapes from various SDKs
    if (!resp) return "";
    if (typeof resp === "string") return resp;
    if (resp.output_text) return resp.output_text;
    if (resp.text) return resp.text;
    if (resp.completion) return resp.completion;
    if (resp.message?.content) {
      const c = resp.message.content;
      if (Array.isArray(c) && c[0]?.text) return c[0].text;
      if (typeof c === "string") return c;
    }
    try { return JSON.stringify(resp); } catch { return String(resp); }
  }

  function parseItemsFromText(text: string): PhraseItem[] {
    // Expect strict JSON; gracefully try to recover
    try {
      const firstBrace = text.indexOf("{");
      const lastBrace = text.lastIndexOf("}");
      const maybe = firstBrace >= 0 && lastBrace > firstBrace ? text.slice(firstBrace, lastBrace + 1) : text;
      const data = JSON.parse(maybe);
      if (data && Array.isArray(data.phrases)) return data.phrases;
    } catch {}
    // Fallback: turn lines into basic items
    const lines = text.split(/\n+/).map((s: string) => s.replace(/^[-*\d\.\)\s]+/, "").trim()).filter(Boolean);
    return lines.slice(0, count).map((l: string) => ({
      phrase_pt: l,
      explain_pt: "Expressão gerada (sem metadados).",
      translate_en: "Generated phrase (no metadata).",
      usage_tip: "Use em conversa casual.",
      register,
      tags: ["fallback"],
      alt_variants: []
    }));
  }

  async function handleGenerate() {
    setLoading(true);
    setError("");
    try {
      const system = systemPrompt;
      const user = buildUserPrompt();

      let respText = "";

      if (!mockMode && hasClaude) {
        const apiUrl = '/api/ai/phrase-maker';
        console.log('🌍 API URL:', apiUrl);
        
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            system,
            messages: [{ role: "user", content: user }],
            temperature,
            max_tokens: 1200
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        
        if (data.success) {
          respText = data.response;
        } else {
          throw new Error(data.error || 'Failed to get AI response');
        }
      } else {
        // Mock JSON so the UX is testable without Claude
        const sample = {
          phrases: Array.from({ length: count }).map((_, i) => ({
            phrase_pt: i % 2 === 0 ? `Bora combinar? ${i+1}` : `Fechou, partiu ${topic || "isso"}!`,
            explain_pt: "Jeito informal de confirmar ou propor algo.",
            translate_en: i % 2 === 0 ? "Wanna set it up?" : "Deal, let's go!",
            usage_tip: "Use com amigos em chat.",
            register,
            tags: [tone ? `tone:${tone}` : "tone:neutral", slang ? "slang" : ""].filter(Boolean),
            alt_variants: ["Pode ser?", "Topa?"]
          }))
        };
        respText = JSON.stringify(sample);
        await new Promise(r => setTimeout(r, 500));
      }

      const parsed = parseItemsFromText(respText);
      setItems(parsed);
    } catch (e) {
      console.error('AI request error:', e);
      setError('Failed to connect to AI. Using mock response.');
      const parsed = parseItemsFromText(getDemoResponse());
      setItems(parsed);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        if (!loading) handleGenerate();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [loading]);

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900">
      <div className="mx-auto max-w-5xl px-4 py-8">
        <header className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Speedy Phrase Maker — pt‑BR ↔ EN</h1>
            <p className="text-sm text-neutral-600 mt-1">Generate concise Brazilian Portuguese phrases with bilingual explanations. <span className="font-medium">Cmd/Ctrl + Enter</span> to run.</p>
          </div>
          <div className="flex items-center gap-3">
            {!hasClaude && !mockMode && (
              <span className="rounded-full bg-amber-100 text-amber-900 px-3 py-1 text-xs">Connect Claude to go live</span>
            )}
            <label className="flex items-center gap-2 text-xs select-none">
              <input type="checkbox" className="scale-110" checked={mockMode} onChange={(e)=>setMockMode(e.target.checked)} />
              Mock mode
            </label>
          </div>
        </header>

        {/* Form */}
        <section className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2 bg-white rounded-2xl shadow-sm border p-4">
            <label className="block text-xs font-medium text-neutral-600">Topic / Intenção (pt ou en)</label>
            <input
              className="mt-1 w-full rounded-xl border px-3 py-2 outline-none focus:ring-2 focus:ring-neutral-800"
              placeholder="ex.: convite pra café, quebrar o gelo, pedir desculpas, flertar…"
              value={topic}
              onChange={(e)=>setTopic(e.target.value)}
            />

            <label className="block text-xs font-medium text-neutral-600 mt-4">Contexto opcional</label>
            <textarea
              className="mt-1 w-full min-h-[84px] rounded-xl border px-3 py-2 outline-none focus:ring-2 focus:ring-neutral-800"
              placeholder="ex.: WhatsApp com amigo brasileiro, tom leve, horário da manhã, evitar gírias pesadas…"
              value={context}
              onChange={(e)=>setContext(e.target.value)}
            />
          </div>

          <div className="bg-white rounded-2xl shadow-sm border p-4 space-y-3">
            <div>
              <label className="block text-xs font-medium text-neutral-600">Tone</label>
              <select className="mt-1 w-full rounded-xl border px-3 py-2" value={tone} onChange={(e)=>setTone(e.target.value)}>
                { ["neutral","playful","romantic","polite","bossy","ironic","friendly","supportive","enthusiastic"]
                  .map(t => <option key={t} value={t}>{t}</option>) }
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-600">Register</label>
              <select className="mt-1 w-full rounded-xl border px-3 py-2" value={register} onChange={(e)=>setRegister(e.target.value)}>
                { ["informal","neutral","formal"].map(r => <option key={r} value={r}>{r}</option>) }
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-600">Level (CEFR)</label>
              <select className="mt-1 w-full rounded-xl border px-3 py-2" value={level} onChange={(e)=>setLevel(e.target.value)}>
                { ["A2","B1","B2"].map(l => <option key={l} value={l}>{l}</option>) }
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-600">Count: {count}</label>
              <input type="range" min={4} max={14} value={count} onChange={(e)=>setCount(parseInt(e.target.value))} className="w-full"/>
            </div>
            <div className="flex items-center justify-between gap-2">
              <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={slang} onChange={(e)=>setSlang(e.target.checked)} /> Usar gíria leve</label>
              <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={businessSafe} onChange={(e)=>setBusinessSafe(e.target.checked)} /> Business‑safe</label>
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-600">Creativity (temp): {temperature.toFixed(2)}</label>
              <input type="range" min={0} max={1} step={0.05} value={temperature} onChange={(e)=>setTemperature(parseFloat(e.target.value))} className="w-full"/>
            </div>
          </div>
        </section>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button onClick={handleGenerate} disabled={loading} className="inline-flex items-center gap-2 rounded-2xl px-4 py-2 bg-neutral-900 text-white disabled:opacity-60">
            <IconWand className="w-4 h-4"/> {loading ? "Generating…" : "Generate"}
          </button>
          <button onClick={surpriseMe} disabled={loading} className="rounded-2xl px-4 py-2 border">
            Surprise me
          </button>
          <button onClick={exportJSON} disabled={!items?.length} className="inline-flex items-center gap-2 rounded-2xl px-4 py-2 border">
            <IconDownload className="w-4 h-4"/> Export JSON
          </button>
          <span className="text-xs text-neutral-500">Tip: Cmd/Ctrl + Enter</span>
        </div>

        {error && (
          <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>
        )}

        {/* Results */}
        <section className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {items?.map((it: PhraseItem, idx: number) => (
            <article key={idx} className="bg-white rounded-2xl shadow-sm border p-4 space-y-2">
              <div className="flex items-start justify-between gap-3">
                <h3 className="text-lg font-semibold leading-snug">{it.phrase_pt}</h3>
                <div className="flex items-center gap-2">
                  <button title="Copy phrase" onClick={()=>copy(it.phrase_pt)} className="p-1 rounded-lg border hover:bg-neutral-50">
                    <IconCopy className="w-4 h-4"/>
                  </button>
                  <button title="Speak (pt-BR)" onClick={()=>speak(it.phrase_pt, "pt-BR")} className="p-1 rounded-lg border hover:bg-neutral-50">
                    <IconPlay className="w-4 h-4"/>
                  </button>
                </div>
              </div>
              <p className="text-sm text-neutral-700">{it.explain_pt}</p>
              <div className="flex items-center justify-between">
                <p className="text-sm italic">EN: {it.translate_en}</p>
                <button title="Speak (EN)" onClick={()=>speak(it.translate_en, "en-US")} className="p-1 rounded-lg border hover:bg-neutral-50">
                  <IconPlay className="w-4 h-4"/>
                </button>
              </div>
              {it.usage_tip && (
                <p className="text-xs text-neutral-600"><span className="font-medium">Tip:</span> {it.usage_tip}</p>
              )}
              <div className="flex flex-wrap gap-1 mt-1">
                <span className="text-[10px] px-2 py-1 rounded-full bg-neutral-100 border">{it.register || register}</span>
                {(it.tags || []).map((t: string, i: number) => (
                  <span key={i} className="text-[10px] px-2 py-1 rounded-full bg-neutral-100 border">{t}</span>
                ))}
              </div>
              {it.alt_variants?.length ? (
                <div className="mt-1 text-xs text-neutral-700">
                  <span className="font-medium">Alternativas:</span> {it.alt_variants.join(" · ")}
                </div>
              ) : null}
            </article>
          ))}
        </section>

        {!items?.length && (
          <div className="mt-10 text-center text-neutral-500">
            <p className="text-sm">No phrases yet. Describe a <span className="font-medium">topic</span> and hit <span className="font-medium">Generate</span>.</p>
            <div className="mt-4 inline-flex items-center gap-2 text-xs">
              <span className="px-2 py-1 rounded-full bg-neutral-200">ex.: convite pra café</span>
              <span className="px-2 py-1 rounded-full bg-neutral-200">quebrar o gelo</span>
              <span className="px-2 py-1 rounded-full bg-neutral-200">elogiar com sutileza</span>
            </div>
          </div>
        )}

        <footer className="mt-10 text-center text-[11px] text-neutral-500">
          Built for fast, bilingual phrase crafting. — If your environment doesn’t expose <code>window.claude.create</code>, enable <b>Mock mode</b> above.
        </footer>
      </div>
    </div>
  );
}
